import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Cube } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset. Please sign in.");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="flex items-center gap-2 mb-8">
          <Cube size={26} weight="bold" />
          <span className="font-heading font-black text-lg tracking-tighter">BuildTrack</span>
        </div>

        {!token ? (
          <div className="border border-destructive p-6" data-testid="reset-invalid">
            <h1 className="font-heading text-2xl font-black tracking-tighter mb-2">Invalid link</h1>
            <p className="text-sm text-muted-foreground mb-6">This reset link is missing its token. Request a new one.</p>
            <Button asChild className="w-full rounded-none"><Link to="/forgot-password">Request reset link</Link></Button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-form">
            <p className="label-caps mb-2">Set new password</p>
            <h1 className="font-heading text-4xl font-black tracking-tighter mb-8">Reset password</h1>

            {error && (
              <div data-testid="reset-error" className="mb-4 border border-destructive px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <Label htmlFor="password" className="label-caps">New password</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  required minLength={6} data-testid="reset-password-input" className="mt-2" placeholder="Min 6 characters" />
              </div>
              <div>
                <Label htmlFor="confirm" className="label-caps">Confirm password</Label>
                <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  required minLength={6} data-testid="reset-confirm-input" className="mt-2" placeholder="Re-enter password" />
              </div>
            </div>

            <Button type="submit" disabled={loading} data-testid="reset-submit-button" className="w-full mt-6 rounded-none h-11">
              {loading ? "Resetting…" : "Reset password"}
            </Button>

            <p className="text-sm text-muted-foreground mt-6 text-center">
              <Link to="/login" className="text-primary font-semibold underline underline-offset-4">Back to sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
